import * as React from "react";
import { observer } from "mobx-react";
import { RecentlyBookedHotelsProps } from "./typings";
import { UitkCardAlohaLink, UitkCardAloha, UitkCardAlohaContentSection } from "uitk-react-cards";
import TruncatedText from "shiitake";
import { UitkRating } from "uitk-react-rating";
import { LocalizedText } from "bernie-l10n";
import { TrackedLink } from "src/components/utility/analytics/TrackedLink";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { UitkFigure } from "uitk-react-images";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkHeading } from "uitk-react-text";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { withStores } from "stores";
import { RecentlyBookedHotelsFlexModuleResult } from "typings/microserviceModels/recently-booked-hotels-flex-module";

export const RecentlyBookedHotels = withStores("flexModuleModelStore")(
  observer((props: RecentlyBookedHotelsProps) => {
    const { flexTemplateNode, flexModuleModelStore } = props;
    const { id, options } = flexTemplateNode;
    const model = flexModuleModelStore.getModel(id) as RecentlyBookedHotelsFlexModuleResult | null;
    /* istanbul ignore next */
    if (!model) {
      return null;
    }

    const items = (model && model.items && model.items.filter((item) => item)) || [];
    const { formatText } = useLocalization();

    if (!model || items.length === 0) {
      return null;
    }
    const { title } = model;

    return (
      <Viewport>
        <ViewMedium>
          <div
            className="RecentlyBookedHotels all-cell-1-1 uitk-cell all-y-padding-three"
            id={id}
            data-fm={options.fmId}
            data-fm-title-id={options.fmTitleId}
          >
            <div className="carouselMediumContainer">
              <UitkHeading level={2} size={600}>
                {title}
              </UitkHeading>
              <IsomorphicCarousel
                itemsVisible={{
                  lg: 2,
                  md: 1,
                  sm: 1,
                }}
                buttonText={{
                  nextButton: formatText("carousel.item.next"),
                  prevButton: formatText("carousel.item.prev"),
                }}
                pageBy={1}
                peekWidth={11}
                className="all-t-padding-two"
              >
                {items.map((recentlyBookedHotel, index) => {
                  return (
                    <div key={recentlyBookedHotel.name}>
                      <RecentlyBookedHotelCard
                        hotelName={recentlyBookedHotel.name}
                        hisUrl={recentlyBookedHotel.url}
                        isMobile={false}
                        starRating={recentlyBookedHotel.rating}
                        lastBookedMessage={formatLastBookedMessage(
                          recentlyBookedHotel.bookedAtNumber,
                          recentlyBookedHotel.bookedAtKey,
                          formatText
                        )}
                        cardIndex={index}
                        imageUrl={recentlyBookedHotel.hotelImageUrl}
                      />
                    </div>
                  );
                })}
              </IsomorphicCarousel>
            </div>
          </div>
        </ViewMedium>
        <ViewSmall>
          <div
            className="RecentlyBookedHotels all-cell-1-1 all-y-padding-three uitk-cell"
            id={id}
            data-fm={options.fmId}
            data-fm-title-id={options.fmTitleId}
          >
            <div className="all-b-margin-three">
              <UitkHeading level={2} size={600}>
                {title}
              </UitkHeading>
            </div>

            <UitkLayoutGrid space="three" columns={1}>
              {items.map((recentlyBookedHotel, index) => {
                return (
                  <UitkLayoutGridItem colSpan={1} key={recentlyBookedHotel.name}>
                    <RecentlyBookedHotelCard
                      hotelName={recentlyBookedHotel.name}
                      hisUrl={recentlyBookedHotel.url}
                      isMobile
                      starRating={recentlyBookedHotel.rating}
                      lastBookedMessage={formatLastBookedMessage(
                        recentlyBookedHotel.bookedAtNumber,
                        recentlyBookedHotel.bookedAtKey,
                        formatText
                      )}
                      cardIndex={index}
                    />
                  </UitkLayoutGridItem>
                );
              })}
            </UitkLayoutGrid>
          </div>
        </ViewSmall>
      </Viewport>
    );
  })
);

interface RecentlyBookedHotelsCardProps {
  hotelName: string;
  hisUrl: string;
  isMobile: boolean;
  starRating: string;
  lastBookedMessage: string;
  cardIndex: number;
  imageUrl?: string;
}

const RecentlyBookedHotelCard = (props: RecentlyBookedHotelsCardProps) => {
  return (
    <UitkCardAloha border={!props.isMobile} className="RecentlyBookedHotelsCard">
      <UitkLayoutFlex>
        {!props.isMobile && (
          <UitkLayoutFlexItem minWidth={150} shrink={0}>
            <UitkCardAlohaContentSection padded={false}>
              <UitkFigure className="all-image-fit-cover" ratio={UitkFigure.AspectRatio.R4_3}>
                <LazyLoadedImage src={props.imageUrl} alt={props.hotelName} />
              </UitkFigure>
            </UitkCardAlohaContentSection>
          </UitkLayoutFlexItem>
        )}
        <UitkLayoutFlexItem>
          <UitkLayoutFlex direction="column" justifyContent="space-between">
            <UitkCardAlohaContentSection>
              <TruncatedText
                lines={2}
                renderFullOnServer
                overflowNode={"\u2026"}
                className="hotelTitle uitk-type-heading-500"
                tagName="h3"
              >
                {props.hotelName}
              </TruncatedText>
              <UitkRating rating={props.starRating} />
            </UitkCardAlohaContentSection>
            <UitkCardAlohaContentSection className="uitk-type-200">
              <span>{props.lastBookedMessage}</span>
            </UitkCardAlohaContentSection>
            <UitkCardAlohaLink>
              <TrackedLink moduleName="recently-booked-hotels" href={props.hisUrl} rfrr={`card-${props.cardIndex}`}>
                <LocalizedText message="hotels.hotel.open.page" data={[props.hotelName]} />
              </TrackedLink>
            </UitkCardAlohaLink>
          </UitkLayoutFlex>
        </UitkLayoutFlexItem>
      </UitkLayoutFlex>
    </UitkCardAloha>
  );
};

const formatLastBookedMessage = (
  lastBookedNumber: number,
  lastBookedLocKey: string,
  formatText: (key: string, ...args: any[]) => string
): string => {
  let locKey = "";
  switch (lastBookedLocKey) {
    case "hotels.hotel.listing.minute": {
      locKey = "recentlyBookedHotels.lastBooking.minute";
      break;
    }
    case "hotels.hotel.listing.minutes": {
      locKey = "recentlyBookedHotels.lastBooking.minutes";
      break;
    }
    case "hotels.hotel.listing.hour": {
      locKey = "recentlyBookedHotels.lastBooking.hour";
      break;
    }
    case "hotels.hotel.listing.hours": {
      locKey = "recentlyBookedHotels.lastBooking.hours";
      break;
    }
  }

  return formatText(locKey, lastBookedNumber);
};

export default RecentlyBookedHotels;
